import { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import {
  daysConversionKey,
  daysInventoryKey,
  daysPayableKey,
  daysSaleKey,
} from '.'
import { LegendItem } from '../../../../../common/chart/legend/LegendItem'
import { PieChartTwoPaths } from '../../../../../common/chart/pieChart/pieChartTwoPaths'
import { Element } from '../../../../../common/html/element'
import { Icon } from '../../../../../common/html/icon'
import { Tabs } from '../../../../../common/tabs'
import { getAppendStyleItemWithCustom } from '../../../../../common/tabs/helper'
import { Tooltip } from '../../../../../common/tooltip'
import { keyBy } from '../../../../../helpers/common'
import { formatVal, isInValidValue } from '../../../../../helpers/value'
import TooltipExpression from '../../common/tooltipExpression'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  getYearTabs,
  handleIconDownload,
} from '../../helper'
import classes from '../../index.module.scss'
import { changeYear, selectYear } from '../../store/slice'
import { selectDataViewByEachYear, selectYears } from './store/slice'

const findMinMax = (data, type) => {
  return data.reduce((value, item) => {
    const values = [value, item.value, item.valueAvg].filter(
      (val) => !isInValidValue(val),
    )

    if (type === 'min') {
      return Math.min(...values)
    }

    return Math.max(...values)
  }, 0)
}

const assetTurnoverId = 'RT0003'

const fixedAssetTurnoverId = 'RT0006'

const EachYear = ({
  id,
  isDynamicReport,
  year: yearSetting,
  isDownloadComponent,
}) => {
  const dispatch = useDispatch()

  const locale = useSelector((state) => state.i18n.locale)
  const year = useSelector(selectYear(id, yearSetting))
  const data = useSelector(selectDataViewByEachYear(year))
  const years = useSelector(selectYears)

  const tabs = getYearTabs(years)

  useEffect(() => {
    if (
      (tabs.length && !year) ||
      !tabs.map((item) => item.key).includes(String(year))
    ) {
      setYear(tabs[tabs.length - 1]?.key)
    }
  }, [tabs.length, year])

  const setYear = (year) => dispatch(changeYear({ id, year }))

  const dataById = keyBy(data, 'id')

  const dataConvert = [
    {
      title: 'financialAnalysis.DAYS_SALE',
      value: dataById[daysSaleKey.VALUE]?.value,
      valueAvg: dataById[daysSaleKey.VALUE_AVG]?.value,
      tooltipContent: (
        <TooltipExpression
          expressions={[
            {
              text: I18n.t('financialAnalysis.DAYS_SALE_1'),
              operator: '=',
            },
            {
              text: I18n.t('financialAnalysis.DAYS_SALE_2'),
              operator: 'x',
              isDivide: true,
              textUnder: I18n.t('financialAnalysis.DAYS_SALE_3'),
            },
            {
              text: I18n.t('financialAnalysis.DAYS_SALE_4'),
            },
          ]}
        />
      ),
    },
    {
      title: 'financialAnalysis.DAYS_INVENTORY',
      value: dataById[daysInventoryKey.VALUE]?.value,
      valueAvg: dataById[daysInventoryKey.VALUE_AVG]?.value,
      tooltipContent: (
        <TooltipExpression
          expressions={[
            {
              text: I18n.t('financialAnalysis.DAYS_INVENTORY_1'),
              operator: '=',
            },
            {
              text: I18n.t('financialAnalysis.DAYS_INVENTORY_2'),
              operator: 'x',
              isDivide: true,
              textUnder: I18n.t('financialAnalysis.DAYS_INVENTORY_3'),
            },
            {
              text: I18n.t('financialAnalysis.DAYS_INVENTORY_4'),
            },
          ]}
        />
      ),
    },
    {
      title: 'financialAnalysis.DAYS_PAYABLE',
      value: dataById[daysPayableKey.VALUE]?.value,
      valueAvg: dataById[daysPayableKey.VALUE_AVG]?.value,
      tooltipContent: (
        <TooltipExpression
          expressions={[
            {
              text: I18n.t('financialAnalysis.DAYS_PAYABLE_1'),
              operator: '=',
            },
            {
              text: I18n.t('financialAnalysis.DAYS_PAYABLE_2'),
              operator: 'x',
              isDivide: true,
              textUnder: I18n.t('financialAnalysis.DAYS_PAYABLE_3'),
            },
            {
              text: I18n.t('financialAnalysis.DAYS_PAYABLE_4'),
            },
          ]}
        />
      ),
    },
    {
      title: 'financialAnalysis.CASH_CONVERSION',
      value: dataById[daysConversionKey.VALUE]?.value,
      valueAvg: dataById[daysConversionKey.VALUE_AVG]?.value,
      tooltipContent: (
        <TooltipExpression
          style={{ alignItems: 'unset' }}
          expressions={[
            {
              text: I18n.t('financialAnalysis.CASH_CONVERSION_1'),
              operator: '=',
              styleContainer: { alignItems: 'unset' },
            },
            {
              text: (
                <>
                  {I18n.t('financialAnalysis.CASH_CONVERSION_2')}
                  <br />
                  +&nbsp;{I18n.t('financialAnalysis.CASH_CONVERSION_3')}
                  <br />
                  -&nbsp;{I18n.t('financialAnalysis.CASH_CONVERSION_4')}
                </>
              ),
              styleText: { textAlign: 'unset' },
              styleContainer: { alignItems: 'unset' },
            },
          ]}
        />
      ),
    },
  ]

  const min = findMinMax(dataConvert, 'min')
  const max = findMinMax(dataConvert, 'max')
  const length = max - min
  const widthText = 130

  const isNoData = !data?.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  return (
    <div
      style={{
        height: getStyleHeightContainer(isDynamicReport, isNoData, 550),
      }}
      className={`${classes.containerPanel} ${classes.assetEachYearContainer} flexColumn`}
    >
      <Element
        style={{
          borderBottom: !isDynamicReport ? '1px solid #B5CEF8' : undefined,
        }}
        className={`dFlex justifyContentEnd ${classes.tabContainer}`}
      >
        {!isDynamicReport && !isDownloadComponent ? (
          <Tabs
            items={tabs}
            tabListClassName={classes.tabs}
            getAppendStyleItem={getAppendStyleItemWithCustom()}
            isScrollContent={false}
            onChange={setYear}
            activeKey={year}
          />
        ) : (
          <>
            {!isNoData && (
              <div className="fw600 py1 w100">
                <Translate value="common.FISCAL_YEAR" />: {year}
              </div>
            )}
          </>
        )}
      </Element>
      {isNoData ? (
        getNoDataComponent(isDynamicReport)
      ) : (
        <div className={!isDynamicReport ? 'h100 dFlex' : 'h100'}>
          <div
            className={
              !isDynamicReport
                ? 'h100 w50 flexColumn pt1 pl4 pr2'
                : 'h50 w100 flexColumn'
            }
          >
            <div className="h100 flexColumn justifyContentBetween">
              {dataConvert.map((item, index) => {
                return (
                  <div key={index}>
                    <div className="dFlex alignItemsCenter">
                      <div className="fw600">
                        <Translate value={item.title} />
                      </div>
                      {!isDynamicReport && !isDownloadComponent && (
                        <Tooltip tooltipContent={item.tooltipContent}>
                          <Icon
                            className="ml1 icon-info-circle"
                            style={{ color: '#9C9C9C', fontSize: 13 }}
                          />
                        </Tooltip>
                      )}
                    </div>
                    {[
                      { key: 'value', color: '#006AA6' },
                      { key: 'valueAvg', color: '#F49966' },
                    ].map(({ key, color }) => {
                      return (
                        <Fragment key={key}>
                          {item[key] && (
                            <div
                              className={`dFlex alignItemsCenter ${classes.mt3}`}
                            >
                              <Element
                                style={{
                                  width: `calc((100% - ${widthText}px) * (${Math.abs(
                                    item[key],
                                  )} / ${length}))`,
                                  background: color,
                                  marginLeft:
                                    item[key] >= 0
                                      ? `calc((100% - ${widthText}px) * (${Math.abs(
                                          min,
                                        )} / ${length}))`
                                      : `calc((100% - ${widthText}px) * (${Math.abs(
                                          min - item[key],
                                        )} / ${length}))`,
                                }}
                                className={classes.item}
                              />
                              <Element
                                className="ml1"
                                style={{ color: '#9C9C9C' }}
                              >
                                {formatVal(item[key], locale)}{' '}
                                <Translate value="financialAnalysis.DAYS" />
                              </Element>
                            </div>
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                )
              })}
            </div>
            <div className="dFlex pt2">
              <LegendItem
                isI18n
                color="#006AA6"
                title="financialAnalysis.COMPANY"
              />
              <div className="ml4">
                <LegendItem
                  isI18n
                  style={{ marginLeft: 20 }}
                  color="#F49966"
                  title="financialAnalysis.INDUSTRY_AVERAGE"
                />
              </div>
            </div>
          </div>
          <div
            className={
              !isDynamicReport ? 'h100 w50 dFlex pl2' : 'h50 w100 dFlex pt3'
            }
          >
            <div className="h100 w50 pt1 dFlex alignItemsCenter justifyContentEnd pr2">
              <PieChartTwoPaths
                width={200}
                height={200}
                pieChartRatio={1}
                pathSmallRatio={0.75}
                backgroundPathFill="#fff"
                backgroundPathOuterRadius={10}
                backgroundPathStrokeWidth={3}
                topPathFill="#fff"
                bottomPathFill="#C06C84"
                backgroundPathStroke="#C06C84"
                bottomPathStrokeWidth={0}
                topPathText={
                  <div className="fw600">
                    <Translate value="financialAnalysis.ASSET_TURNOVER_1" />
                    <br />
                    <Translate value="financialAnalysis.ASSET_TURNOVER_2" />
                  </div>
                }
                bottomPathText={
                  <Element
                    style={{ fontSize: 24, color: '#FFFFFF' }}
                    className="fw600"
                  >
                    {formatVal(dataById[assetTurnoverId]?.value, locale)}
                  </Element>
                }
                isBottomPathSmall={false}
              />
            </div>
            <div className="h100 w50 pt1 dFlex alignItemsCenter pl2">
              <PieChartTwoPaths
                width={200}
                height={200}
                pieChartRatio={1}
                pathSmallRatio={0.75}
                backgroundPathFill="#FFFFFF"
                backgroundPathOuterRadius={10}
                backgroundPathStrokeWidth={3}
                topPathFill="#FFFFFF"
                bottomPathFill="#00A8B5"
                backgroundPathStroke="#00A8B5"
                bottomPathStrokeWidth={0}
                topPathText={
                  <div className="fw600">
                    <Translate value="financialAnalysis.FIXED_ASSET_TURNOVER_1" />
                    <br />
                    <Translate value="financialAnalysis.FIXED_ASSET_TURNOVER_2" />
                  </div>
                }
                bottomPathText={
                  <Element
                    style={{ fontSize: 24, color: '#FFFFFF' }}
                    className="fw600"
                  >
                    {formatVal(dataById[fixedAssetTurnoverId]?.value, locale)}
                  </Element>
                }
                isBottomPathSmall={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EachYear
